// D:\docker-files\11.AI_trade3\3000.dashboard\src\pages\AccountDeletionPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, 
  User, 
  Mail, 
  Lock, 
  ArrowLeft, 
  Trash2, 
  Shield,
  Database,
  CreditCard,
  Brain
} from 'lucide-react';
import { userService } from '../services/api';
import { getErrorMessage } from '../utils/helpers';
import { logout } from '../utils/auth';
import toast from 'react-hot-toast';

const AccountDeletionPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: 확인, 2: 비밀번호 입력, 3: 최종 확인
  const [formData, setFormData] = useState({
    password: '',
    confirmation: '',
    reason: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const [canProceed, setCanProceed] = useState(false);

  // 사용자 정보 로드
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const response = await userService.getCurrentUser();
        setUserInfo(response);
      } catch (error) {
        console.error('사용자 정보 로드 실패:', error);
        navigate('/account');
      }
    };
    loadUserInfo();
  }, [navigate]);

  // 최종 확인 단계에서 카운트다운
  useEffect(() => {
    if (step === 3 && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 3 && countdown === 0) {
      setCanProceed(true);
    }
  }, [step, countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNextStep = () => {
    setError('');
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (!formData.password) {
        setError('현재 비밀번호를 입력해주세요.');
        return;
      }
      setStep(3);
      setCountdown(10); // 카운트다운 시작
      setCanProceed(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!canProceed) return;

    setIsLoading(true);
    setError('');

    try {
      await userService.deleteAccount({
        password: formData.password,
        reason: formData.reason
      });

      toast.success('계정이 성공적으로 삭제되었습니다.');
      
      // 로그아웃 처리
      logout();
      
      // 메인 페이지로 이동
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error) {
      setError(getErrorMessage(error));
      toast.error('계정 삭제 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const deletionWarnings = [
    {
      icon: <Database className="w-5 h-5 text-red-500" />,
      title: "모든 데이터 삭제",
      description: "계정 정보, 거래 내역, AI 분석 데이터가 영구적으로 삭제됩니다."
    },
    {
      icon: <CreditCard className="w-5 h-5 text-red-500" />,
      title: "투자 정보 삭제",
      description: "보유 종목, 포트폴리오, 투자 설정이 모두 삭제됩니다."
    },
    {
      icon: <Brain className="w-5 h-5 text-red-500" />,
      title: "AI 추천 이력 삭제",
      description: "개인화된 AI 분석 및 추천 이력이 모두 삭제됩니다."
    },
    {
      icon: <Shield className="w-5 h-5 text-red-500" />,
      title: "복구 불가",
      description: "삭제된 데이터는 복구할 수 없습니다."
    }
  ];

  const deletionReasons = [
    "서비스가 더 이상 필요하지 않음",
    "다른 서비스를 사용하기로 함",
    "개인정보 보호 우려",
    "서비스 품질에 불만족",
    "기타"
  ];

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <AlertTriangle className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            계정 탈퇴
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {step === 1 && "탈퇴하기 전에 다음 사항을 확인해주세요"}
            {step === 2 && "본인 확인을 위해 비밀번호를 입력해주세요"}
            {step === 3 && "정말로 계정을 삭제하시겠습니까?"}
          </p>
        </div>

        {/* 단계별 콘텐츠 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          
          {/* Step 1: 경고 및 안내 */}
          {step === 1 && (
            <div className="space-y-6">
              {/* 사용자 정보 */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <User className="w-8 h-8 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">현재 계정</h3>
                    <p className="text-sm text-gray-600">{userInfo.email}</p>
                    <p className="text-xs text-gray-500">
                      가입일: {new Date(userInfo.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* 경고 사항 */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-red-600 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  계정 삭제 시 삭제되는 정보
                </h3>
                
                <div className="grid gap-4">
                  {deletionWarnings.map((warning, index) => (
                    <div key={index} className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg border border-red-200">
                      {warning.icon}
                      <div>
                        <h4 className="font-medium text-gray-900">{warning.title}</h4>
                        <p className="text-sm text-gray-600">{warning.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 탈퇴 사유 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  탈퇴 사유를 선택해주세요
                </label>
                <select
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">선택해주세요</option>
                  {deletionReasons.map((reason, index) => (
                    <option key={index} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => navigate('/settings')}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  설정으로 돌아가기
                </button>
                <button
                  onClick={handleNextStep}
                  disabled={!formData.reason}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  계속 진행
                </button>
              </div>
            </div>
          )}

          {/* Step 2: 비밀번호 확인 */}
          {step === 2 && (
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="text-center">
                <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">본인 확인</h3>
                <p className="text-sm text-gray-600">
                  계정 보안을 위해 현재 비밀번호를 입력해주세요
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  현재 비밀번호
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-3 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="현재 비밀번호를 입력하세요"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  이전
                </button>
                <button
                  onClick={handleNextStep}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors"
                >
                  다음
                </button>
              </div>
            </div>
          )}

          {/* Step 3: 최종 확인 */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <Trash2 className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-red-600 mb-2">
                  정말로 계정을 삭제하시겠습니까?
                </h3>
                <p className="text-sm text-gray-600">
                  이 작업은 되돌릴 수 없습니다. 모든 데이터가 영구적으로 삭제됩니다.
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-sm font-medium text-red-700">
                    {countdown > 0 ? (
                      `${countdown}초 후에 삭제가 가능합니다`
                    ) : (
                      "이제 계정을 삭제할 수 있습니다"
                    )}
                  </span>
                </div>
              </div>

              {/* 삭제할 정보 요약 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">삭제될 정보:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 계정 정보 및 프로필</li>
                  <li>• 모든 거래 내역</li>
                  <li>• AI 분석 및 추천 데이터</li>
                  <li>• 포트폴리오 설정</li>
                  <li>• API 키 및 연동 정보</li>
                </ul>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  이전
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={!canProceed || isLoading}
                  className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      삭제 중...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5 mr-2" />
                      계정 영구 삭제
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 진행 상태 표시 */}
        <div className="flex justify-center space-x-2">
          {[1, 2, 3].map((stepNumber) => (
            <div
              key={stepNumber}
              className={`w-3 h-3 rounded-full transition-colors ${
                stepNumber === step
                  ? 'bg-red-600'
                  : stepNumber < step
                  ? 'bg-red-400'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccountDeletionPage;